import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Avatar,
  Divider,
  Paper,
} from '@mui/material';
import {
  ArrowBack,
  Edit as EditIcon,
  Apartment as ApartmentIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  AccountBalance as BankIcon,
} from '@mui/icons-material';

const ApartmentDetails = () => {
  const history = useHistory();
  const location = useLocation();
  const apartment = location.state?.apartment;

  if (!apartment) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5">Apartment not found</Typography>
        <Button onClick={() => history.push('/app/project/ProjectPricing')}>
          Back to List
        </Button>
      </Box>
    );
  }

  const handleEdit = () => {
    history.push({
      pathname: '/app/project/apartment/edit',
      state: { apartment: apartment }
    });
  };

  const handleBack = () => {
    history.push('/app/project/ProjectPricing');
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={handleBack}
              sx={{ textTransform: 'none' }}
            >
              Back
            </Button>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                {apartment.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Apartment ID: {apartment.apartmentId}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            sx={{ textTransform: 'none', px: 3 }}
          >
            Edit Apartment
          </Button>
        </Box>
      </Box>

      {/* Status Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Configuration
              </Typography>
              <Chip
                label={apartment.badge}
                sx={{
                  mt: 1,
                  backgroundColor: apartment.badgeColor || '#4CAF50',
                  color: '#fff',
                  fontWeight: 600,
                }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Status
              </Typography>
              <Chip
                label={apartment.status}
                color={apartment.status === 'Active' ? 'success' : 'default'}
                sx={{ mt: 1, fontWeight: 600 }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Type
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>
                {apartment.type}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Commission
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, mt: 1, color: '#536DFE' }}>
                {apartment.commission}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Details */}
      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <ApartmentIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Basic Information
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Apartment ID
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {apartment.apartmentId}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Apartment Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {apartment.name}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Developer
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {apartment.developer}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Type
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {apartment.type}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <PhoneIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Contact Information
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Contact Person
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {apartment.contactPerson}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <EmailIcon fontSize="small" color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {apartment.email}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <PhoneIcon fontSize="small" color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {apartment.phone}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Apartment Specifications */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Specifications
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Configuration
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {apartment.configuration}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Carpet Area
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {apartment.carpetArea}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Floor Range
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {apartment.floor}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Parking
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {apartment.parking}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Total Units
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {apartment.totalUnits}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Circle Rate
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {apartment.guidelineValue}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Project Details */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Project Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Start Date
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {apartment.startDate?.split('|')[0] || 'TBD'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Completion Date
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {apartment.completeDate?.split('|')[0] || 'TBD'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  RERA Number
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {apartment.reraNo}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Possession Date
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {apartment.possessionDate}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Address Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <LocationIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Address Information
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Full Address
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {apartment.address}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  City
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {apartment.city}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  State
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {apartment.state}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Bank Details */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <BankIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Bank Details
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Bank Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {apartment.bankName}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Account Number
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {apartment.accountNumber}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  IFSC Code
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {apartment.ifscCode}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Amenities */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Amenities
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body1">
              {apartment.amenities}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ApartmentDetails;

